import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	ReactNode
} from 'react';

interface SidebarState {
	// Track which menu items are expanded (by titleKey) - unified for both modes
	expandedItems: Set<string>;
}

interface SidebarContextType {
	sidebarState: SidebarState;
	toggleItemExpanded: (titleKey: string) => void;
	setItemExpanded: (titleKey: string, expanded: boolean) => void;
	isItemExpanded: (titleKey: string) => boolean;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const STORAGE_KEY = 'sidebar-state';

export const SidebarProvider: React.FC<{ children: ReactNode }> = ({
	children
}) => {
	const [sidebarState, setSidebarState] = useState<SidebarState>(() => {
		// Load from localStorage
		try {
			const saved = localStorage.getItem(STORAGE_KEY);
			if (saved) {
				const parsed = JSON.parse(saved);
				return {
					expandedItems: new Set(parsed.expandedItems || [])
				};
			}
		} catch (error) {
			console.warn(
				'Failed to load sidebar state from localStorage:',
				error
			);
		}

		return {
			expandedItems: new Set<string>()
		};
	});

	// Save to localStorage whenever state changes
	useEffect(() => {
		try {
			const stateToSave = {
				expandedItems: Array.from(sidebarState.expandedItems)
			};
			localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
		} catch (error) {
			console.warn(
				'Failed to save sidebar state to localStorage:',
				error
			);
		}
	}, [sidebarState]);

	const toggleItemExpanded = (titleKey: string) => {
		setSidebarState(prev => {
			const newExpandedItems = new Set(prev.expandedItems);
			if (newExpandedItems.has(titleKey)) {
				newExpandedItems.delete(titleKey);
			} else {
				newExpandedItems.add(titleKey);
			}
			return {
				...prev,
				expandedItems: newExpandedItems
			};
		});
	};

	const setItemExpanded = (titleKey: string, expanded: boolean) => {
		setSidebarState(prev => {
			const newExpandedItems = new Set(prev.expandedItems);
			if (expanded) {
				newExpandedItems.add(titleKey);
			} else {
				newExpandedItems.delete(titleKey);
			}
			return {
				...prev,
				expandedItems: newExpandedItems
			};
		});
	};

	const isItemExpanded = (titleKey: string) => {
		return sidebarState.expandedItems.has(titleKey);
	};

	const value: SidebarContextType = {
		sidebarState,
		toggleItemExpanded,
		setItemExpanded,
		isItemExpanded
	};

	return (
		<SidebarContext.Provider value={value}>
			{children}
		</SidebarContext.Provider>
	);
};

export const useSidebar = () => {
	const context = useContext(SidebarContext);
	if (context === undefined) {
		throw new Error('useSidebar must be used within a SidebarProvider');
	}
	return context;
};
